import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { VersionUpdaterService } from '../version-updater.service';

export interface AssignShopsDialogData {
  versionId: string;
  version: any;
  versionNumber: string;
  assignedShops: any;
}

interface ShopItem {
  id: string;
  code: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  country: string;
  shop_type: string;
  isSelected: boolean;
}

@Component({
  selector: 'app-assign-shops',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './assign-shops.component.html',
  styleUrl: './assign-shops.component.scss'
})
export class AssignShopsComponent implements OnInit {
  
  allShops: ShopItem[] = [];
  filteredShops: ShopItem[] = [];
  selectedShops: ShopItem[] = [];
  versionNumber: string = '';
  searchTerm: string = '';
  showSelectedOnly: boolean = false;
  isSaving: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AssignShopsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssignShopsDialogData,
    private versionUpdaterService: VersionUpdaterService
  ) {}

  ngOnInit(): void {
    console.log('Assign shops dialog opened with data:', this.data);
    this.versionNumber = this.data.versionNumber || this.data.versionId || '';
    this.processShopsData();
  }

  private processShopsData(): void {
    if (this.data.assignedShops && this.data.assignedShops.data) {
      const allShopsData = this.data.assignedShops.data.all_shops || [];
      const assignedShopsData = this.data.assignedShops.data.assigned_shops || [];

      // Create a set of assigned shop IDs for quick lookup
      const assignedShopIds = new Set(
        assignedShopsData.map((shop: any) => shop.shop_id)
      );

      // Process all shops and mark selected ones
      this.allShops = allShopsData.map((shop: any) => ({
        id: shop.id,
        code: shop.code,
        name: shop.name,
        description: shop.description,
        address: shop.address,
        city: shop.city,
        state: shop.state,
        country: shop.country,
        shop_type: shop.shop_type,
        isSelected: assignedShopIds.has(shop.id)
      }));

      // Set initially selected shops
      this.selectedShops = this.allShops.filter(shop => shop.isSelected);
      
      // Initialize filtered shops
      this.filteredShops = [...this.allShops];
      
      console.log('Processed shops:', this.allShops);
      console.log('Initially selected shops:', this.selectedShops);
    }
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.allShops];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(shop => 
        shop.name.toLowerCase().includes(searchLower) ||
        shop.code.toLowerCase().includes(searchLower) ||
        (shop.description && shop.description.toLowerCase().includes(searchLower)) ||
        (shop.address && shop.address.toLowerCase().includes(searchLower)) ||
        (shop.city && shop.city.toLowerCase().includes(searchLower))
      );
    }

    // Apply "Show Selected Only" filter
    if (this.showSelectedOnly) {
      filtered = filtered.filter(shop => shop.isSelected);
    }

    this.filteredShops = filtered;
  }

  onShopSelectionChange(shop: ShopItem): void {
    if (shop.isSelected) {
      // Add to selected shops if not already present
      if (!this.selectedShops.find(s => s.id === shop.id)) {
        this.selectedShops.push(shop);
      }
    } else {
      // Remove from selected shops
      this.selectedShops = this.selectedShops.filter(s => s.id !== shop.id);
    }
    console.log('Selected shops updated:', this.selectedShops);
    
    // Reapply filters to update the view if "Show Selected Only" is active
    if (this.showSelectedOnly) {
      this.applyFilters();
    }
  }

  onSave(): void {
    if (this.isSaving) return; // Prevent double submission
    
    const selectedShopIds = this.selectedShops.map(shop => parseInt(shop.id));
    
    // Create payload in the specified format
    const payload = {
      versionNo: this.versionNumber,
      shopsList: selectedShopIds
    };
    
    console.log('Save payload:', payload);
    
    // Show saving state
    this.isSaving = true;
    
    // Call the API to update assigned shops
    this.versionUpdaterService.updateAssignedShops(payload).subscribe({
      next: (response) => {
        this.isSaving = false;
        console.log('Shops updated successfully:', response);
        
        // Close dialog with success result
        this.dialogRef.close({ 
          assigned: true, 
          payload: payload,
          apiResponse: response,
          selectedShops: this.selectedShops,
          selectedShopIds: selectedShopIds,
          versionNumber: this.versionNumber,
          message: `${this.selectedShops.length} shops assigned to version ${this.versionNumber}`
        });
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating assigned shops:', error);
        
        // Close dialog with error result
        this.dialogRef.close({ 
          assigned: false, 
          error: error,
          message: `Error assigning shops to version ${this.versionNumber}`
        });
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
